import React from 'react';
import { Form, Input, Row, Col, Typography, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import './LabComponentFields.css';

const { Text } = Typography;

// The analyte boxes inside a panel.
//
// A CBC is fourteen numbers, a G.U.E fifteen; the paper request form the doctor
// is coming from has a named line for each, and one free-text "Result" field is
// a step backwards from that. So a panel opens into its own named boxes, each
// showing its unit and normal range.
//
// The names, units and ranges are read from the form values rather than looked
// up from the library, because they were copied onto the record when the test
// was ordered. That is deliberate: a result printed in two years still says what
// it was measured in and what the range was taken to be, even if the library has
// been revised since.
//
// Two paths, because antd resolves them from different places:
//
//   listName  where Form.List looks. RELATIVE to the enclosing Form.List, if
//             there is one - inside the visit form's `labTests` list that is
//             [rowIndex, 'components'], not the full path. Getting this wrong
//             double-prefixes it (labTests.labTests.0.components), the list
//             finds nothing, and the panel silently renders no boxes at all.
//   metaPath  where form.getFieldValue looks. Always ABSOLUTE from the form
//             root, because that is a form-instance method with no context to
//             resolve against. Defaults to listName, which is right whenever the
//             list is not nested (the lab's own result form).
const LabComponentFields = ({ form, listName, metaPath = listName, disabled = false }) => {
    const { t } = useTranslation();

    return (
        <Form.List name={listName}>
            {(fields) => {
                if (fields.length === 0) return null;

                return (
                    <div className="lab-components">
                        <div className="lab-components-title">
                            {t('testComponents') || 'Results'}
                        </div>
                        <Row gutter={[8, 0]}>
                            {fields.map(({ key, name, ...restField }) => {
                                const meta = form.getFieldValue([...metaPath, name]) || {};
                                const label = meta.name || meta.code;

                                return (
                                    <Col xs={12} sm={8} lg={6} key={key}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'value']}
                                            label={
                                                <Tooltip
                                                    title={meta.referenceRange
                                                        ? `${t('referenceRange')}: ${meta.referenceRange}`
                                                        : undefined}
                                                >
                                                    <span className="lab-component-label">{label}</span>
                                                </Tooltip>
                                            }
                                            className="lab-component-item"
                                        >
                                            <Input
                                                // The unit is a fixed suffix rather than a
                                                // placeholder: it must stay visible once a
                                                // number has been typed over it.
                                                suffix={meta.unit
                                                    ? <span className="lab-component-unit">{meta.unit}</span>
                                                    : null}
                                                disabled={disabled}
                                            />
                                        </Form.Item>
                                        {meta.referenceRange && (
                                            <Text type="secondary" className="lab-component-range">
                                                {meta.referenceRange}
                                            </Text>
                                        )}
                                        {/* The three descriptive fields ride along hidden, so
                                            what gets saved carries its own units and range and
                                            does not have to be resolved again on the way out. */}
                                        <Form.Item {...restField} name={[name, 'code']} hidden><Input /></Form.Item>
                                        <Form.Item {...restField} name={[name, 'name']} hidden><Input /></Form.Item>
                                        <Form.Item {...restField} name={[name, 'unit']} hidden><Input /></Form.Item>
                                        <Form.Item {...restField} name={[name, 'referenceRange']} hidden><Input /></Form.Item>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                );
            }}
        </Form.List>
    );
};

export default LabComponentFields;
